import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import leadsService, { LeadFilters, CreateLeadDto } from '../../services/leadsService';
import { Lead, LeadStats } from '../../types';

interface LeadsState {
  list:     Lead[];
  stats:    LeadStats | null;
  selected: Lead | null;
  loading:  boolean;
  error:    string | null;
}

const initialState: LeadsState = {
  list: [], stats: null, selected: null, loading: false, error: null,
};

const upsert = (list: Lead[], lead: Lead) => {
  const i = list.findIndex(x => x._id === lead._id);
  if (i !== -1) list[i] = lead; else list.unshift(lead);
};

export const fetchLeadStats = createAsyncThunk('leads/stats',   async (_, { rejectWithValue }) => { try { return await leadsService.getStats(); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const fetchLeads     = createAsyncThunk('leads/fetchAll',async (f: LeadFilters, { rejectWithValue }) => { try { return await leadsService.getAll(f); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const createLead     = createAsyncThunk('leads/create',  async (d: CreateLeadDto, { rejectWithValue }) => { try { return await leadsService.create(d); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const updateLead     = createAsyncThunk('leads/update',  async ({ id, data }: { id: string; data: Partial<CreateLeadDto> }, { rejectWithValue }) => { try { return await leadsService.update(id, data); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const deleteLead     = createAsyncThunk('leads/delete',  async (id: string, { rejectWithValue }) => { try { await leadsService.delete(id); return id; } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const convertLead    = createAsyncThunk('leads/convert', async (id: string, { rejectWithValue }) => { try { return await leadsService.convert(id); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });

const leadsSlice = createSlice({
  name: 'leads', initialState,
  reducers: {
    setSelected: (state, action: PayloadAction<Lead | null>) => { state.selected = action.payload; },
    clearError:  (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeadStats.fulfilled, (s, a) => { s.stats = a.payload.data ?? null; })
      .addCase(fetchLeads.pending,   (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLeads.fulfilled, (s, a) => { s.loading = false; s.list = a.payload.data ?? []; })
      .addCase(fetchLeads.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(createLead.fulfilled, (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(updateLead.fulfilled, (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); if (s.selected?._id === a.payload.data._id) s.selected = a.payload.data; } })
      .addCase(deleteLead.fulfilled, (s, a) => { s.list = s.list.filter(l => l._id !== a.payload); if (s.selected?._id === a.payload) s.selected = null; })
      .addCase(convertLead.fulfilled,(s, a) => { if (a.payload.data?.lead) upsert(s.list, a.payload.data.lead); });
  },
});

export const { setSelected, clearError } = leadsSlice.actions;
export default leadsSlice.reducer;
